import { motion } from 'framer-motion';
import { useState } from 'react';
import { TrendingUp, Award, Target, Users, Clock, Dumbbell, ArrowRight } from 'lucide-react';

/**
 * Portfolio Page Component
 * Showcasing client transformations, success stories, and program results
 */
export default function Portfolio() {
  return (
    <div className="min-h-screen">
      <HeroSection />
      <TransformationsSection />
      <SuccessMetricsSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="relative py-20 bg-gradient-to-br from-primary-700 via-primary-600 to-accent-500 text-white overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-300 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Award className="w-4 h-4" />
            <span className="text-sm font-semibold">Real Results, Real People</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            Client Transformations
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Inspiring success stories from clients who committed to their goals and achieved remarkable results
          </p>
        </motion.div>
      </div>
    </section>
  );
}

// Transformations Section - Featured success stories
function TransformationsSection() {
  const [activeFilter, setActiveFilter] = useState('all');

  const transformations = [
    {
      id: 1,
      name: 'Sarah M.',
      category: 'weight-loss',
      duration: '16 weeks',
      result: 'Lost 32lbs',
      image: '/api/placeholder/400/500',
      metrics: {
        weightLost: '32 lbs',
        bodyFat: '-12%',
        strength: '+45%'
      },
      testimonial: 'Luke helped me completely transform my relationship with fitness and nutrition.',
      program: 'Online 1-on-1 Coaching'
    },
    {
      id: 2,
      name: 'Mike R.',
      category: 'muscle-gain',
      duration: '20 weeks',
      result: 'Gained 18lbs muscle',
      image: '/api/placeholder/400/500',
      metrics: {
        muscleGained: '18 lbs',
        bodyFat: '-5%',
        strength: '+65%'
      },
      testimonial: 'The personalized program was exactly what I needed to break through my plateau.',
      program: 'In-Person Training'
    },
    {
      id: 3,
      name: 'Jennifer A.',
      category: 'endurance',
      duration: '24 weeks',
      result: 'Marathon finisher',
      image: '/api/placeholder/400/500',
      metrics: {
        distance: '26.2 miles',
        time: '4:15:30',
        weight: '-15 lbs'
      },
      testimonial: 'From couch to marathon! Luke\'s coaching made the impossible possible.',
      program: 'Online 1-on-1 Coaching'
    },
    {
      id: 4,
      name: 'David L.',
      category: 'weight-loss',
      duration: '12 weeks',
      result: 'Lost 28lbs',
      image: '/api/placeholder/400/500',
      metrics: {
        weightLost: '28 lbs',
        bodyFat: '-10%',
        energy: '+100%'
      },
      testimonial: 'Best investment I\'ve made in myself. Life-changing results!',
      program: 'VIP Transformation'
    },
    {
      id: 5,
      name: 'Amanda K.',
      category: 'muscle-gain',
      duration: '18 weeks',
      result: 'Gained 12lbs muscle',
      image: '/api/placeholder/400/500',
      metrics: {
        muscleGained: '12 lbs',
        bodyFat: '-8%',
        strength: '+55%'
      },
      testimonial: 'Finally achieved the toned, strong physique I always wanted.',
      program: 'In-Person Training'
    },
    {
      id: 6,
      name: 'Carlos M.',
      category: 'endurance',
      duration: '16 weeks',
      result: 'Triathlon complete',
      image: '/api/placeholder/400/500',
      metrics: {
        swim: '1.2mi',
        bike: '56mi',
        run: '13.1mi'
      },
      testimonial: 'Luke\'s guidance helped me achieve something I never thought possible.',
      program: 'Online 1-on-1 Coaching'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Results' },
    { id: 'weight-loss', label: 'Weight Loss' },
    { id: 'muscle-gain', label: 'Muscle Gain' },
    { id: 'endurance', label: 'Endurance' }
  ];

  const filteredTransformations = activeFilter === 'all'
    ? transformations
    : transformations.filter(t => t.category === activeFilter);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {filters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                activeFilter === filter.id
                  ? 'bg-gradient-to-r from-primary-600 to-accent-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Transformations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTransformations.map((transformation, index) => (
            <motion.div
              key={transformation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group"
            >
              {/* Image */}
              <div className="relative h-64 bg-gradient-to-br from-primary-400 to-accent-400 overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Dumbbell className="w-16 h-16 mx-auto mb-2 opacity-50" />
                    <p className="text-sm font-semibold">Before & After</p>
                  </div>
                </div>

                {/* Result Badge */}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-full">
                  <p className="text-sm font-bold text-primary-600">{transformation.result}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">{transformation.name}</h3>
                  <div className="flex items-center gap-1 text-accent-500">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-semibold">{transformation.duration}</span>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {Object.entries(transformation.metrics).map(([key, value], idx) => (
                    <div key={idx} className="text-center p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                      <p className="text-sm font-bold text-primary-600">{value}</p>
                    </div>
                  ))}
                </div>

                <p className="text-gray-600 italic mb-4">
                  "{transformation.testimonial}"
                </p>

                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {transformation.program}
                  </span>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Success Metrics Section
function SuccessMetricsSection() {
  const metrics = [
    {
      icon: <Users className="w-8 h-8" />,
      value: '500+',
      label: 'Clients Transformed',
      color: 'primary'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      value: '12,000+',
      label: 'Pounds Lost',
      color: 'accent'
    },
    {
      icon: <Target className="w-8 h-8" />,
      value: '98%',
      label: 'Success Rate',
      color: 'primary'
    },
    {
      icon: <Award className="w-8 h-8" />,
      value: '4.9/5',
      label: 'Average Rating',
      color: 'accent'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Proven Track Record
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Numbers that demonstrate our commitment to client success
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {metrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center p-6 bg-gray-50 rounded-2xl hover:shadow-xl transition-all duration-300"
            >
              <div className={`inline-flex items-center justify-center w-16 h-16 mb-4 rounded-xl bg-gradient-to-br ${
                metric.color === 'primary' ? 'from-primary-500 to-primary-600' : 'from-accent-500 to-accent-600'
              } text-white`}>
                {metric.icon}
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{metric.value}</div>
              <div className="text-gray-600 font-medium">{metric.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const testimonials = [
    {
      name: 'Rachel Thompson',
      role: 'Online Coaching Client',
      content: 'Luke\'s coaching changed my life. Not just my body, but my entire mindset around health and fitness. The daily support and accountability were game-changers.',
      rating: 5
    },
    {
      name: 'Jason Martinez',
      role: 'In-Person Training Client',
      content: 'Best personal trainer I\'ve ever worked with. Luke truly cares about results and pushes you to be your best while keeping training fun and sustainable.',
      rating: 5
    },
    {
      name: 'Emily Chen',
      role: 'VIP Transformation Client',
      content: 'The VIP program was worth every penny. Complete transformation in body, confidence, and lifestyle. I couldn\'t have done it without Luke\'s expertise and support.',
      rating: 5
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            What Clients Say
          </h2>
          <p className="text-xl text-gray-600">
            Hear directly from people who've transformed their lives
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl shadow-lg"
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Award key={i} className="w-5 h-5 fill-accent-500 text-accent-500" />
                ))}
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed italic">
                "{testimonial.content}"
              </p>

              <div>
                <div className="font-bold text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-600">{testimonial.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection() {
  return (
    <section className="py-20 bg-gradient-to-r from-primary-700 to-primary-600">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join hundreds of satisfied clients who have transformed their lives with LC Performance coaching.
          </p>

          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl hover:scale-105 transform transition-all duration-300"
          >
            Start Your Transformation
            <ArrowRight className="w-5 h-5" />
          </a>

          <p className="mt-6 text-white/70 text-sm">
            Free consultation • No pressure • Real results
          </p>
        </motion.div>
      </div>
    </section>
  );
}
